import { useEffect, useMemo, useState } from "react";
import {
	MRT_ShowHideColumnsButton,
	MRT_ToggleFiltersButton,
	MRT_ToggleGlobalFilterButton,
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
	Download,
	FileDownloadOutlined,
	FileUploadOutlined,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { download, generateCsv, mkConfig } from "export-to-csv";

import "./SeempBody.scss";

import { Button, Icon, ICONS } from "abs-ui-components";

// import { emitToasterMsg } from "../../services/toasterUtility";
import { setVesselRevisionDetails } from "../../store/seemp/seemp.slice";
import { getQueryData } from "../../services/utilities";
import { convertLocaltoUTCFormat } from "../../services/DateFormatUtility";

const SeempBody = ({ activeTab }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [seempData, setSeempData] = useState([]);
	const [seempRowSelection, setSeempRowSelection] = useState({});
	const [seempColumnFilters, setSeempColumnFilters] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [seempGlobalFilter, setSeempGlobalFilter] = useState("");
	const [seempSort, setSeempSort] = useState([]);
	const [loading, setLoading] = useState(false);
	const [seempPagination, setSeempPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});
	const [selectAll, setSelectAll] = useState(false);

	useEffect(() => {
		let sortOptions = seempSort.map((x) => ({
			SortField: x.id,
			IsAscending: !x.desc,
		}));

		if (seempColumnFilters.length > 0) {
			filterVessel(
				seempColumnFilters,
				seempPagination.pageIndex,
				seempPagination.pageSize,
				sortOptions
			);
		} else {
			searchVessels(
				seempGlobalFilter,
				seempPagination.pageIndex,
				seempPagination.pageSize,
				sortOptions
			);
		}
	}, [
		seempGlobalFilter,
		seempPagination.pageIndex,
		seempPagination.pageSize,
		seempColumnFilters,
		seempSort,
	]);

	const searchVessels = (
		searchStr = "",
		pageIndex = 0,
		pageSize = 25,
		sortOptions
	) => {
		let requestPayload = {
			entity: "SEEMPDTL",
			query: {
				conditions: [
					{
						type: "and",
						queries: [],
					},
					{
						type: "or",
						queries: [
							{
								field: "Wcn",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "CompanyName",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "ImoNumber",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "VesselName",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "VesselType",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "Flag",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "ReportNumber",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "Status",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "SubmissionDate",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "VerifiedDate",
								operation: "contains",
								value: searchStr,
							},
							{
								field: "AbsContact",
								operation: "contains",
								value: searchStr,
							},
						],
					},
				],
			},
			pagination: {
				page: pageIndex + 1,
				pageSize: pageSize,
			},
		};

		if (sortOptions) {
			requestPayload = { ...requestPayload, sortOptions };
		}

		setLoading(true);
		getQueryData(requestPayload)
			.then((response) => {
				setSeempData(response.data?.Data);
				setRowCount(response.data.Pagination.TotalItems);
				setLoading(false);
			})
			.catch((error) => {
				setSeempData([]);
				setLoading(false);
			});
	};

	const filterVessel = (
		columnFilterValue,
		pageIndex = 0,
		pageSize = 25,
		sortOptions
	) => {
		if (columnFilterValue) {
			const columnFilterPayload = columnFilterValue.map((element) => {
				let singleQuery = {
					field: element.id,
					operation: "contains",
					value: element.value,
				};

				return singleQuery;
			});

			let requestPayload = {
				entity: "SEEMPDTL",
				query: {
					conditions: [
						{
							type: "and",
							queries: [...columnFilterPayload],
						},
					],
				},
				pagination: {
					page: pageIndex + 1,
					pageSize: pageSize,
				},
			};

			if (sortOptions) {
				requestPayload = { ...requestPayload, sortOptions };
			}

			setLoading(true);
			getQueryData(requestPayload)
				.then((response) => {
					if (response.data.Data) {
						setSeempData(response.data.Data);
						setRowCount(response.data.Pagination.TotalItems);
					}
					setLoading(false);
				})
				.catch((error) => {
					setSeempData([]);
					setLoading(false);
				});
		}
	};

	const handleMyFleetTableRowClick = (row) => {
		dispatch(
			setVesselRevisionDetails({
				vesselName: row.original.VesselName,
			})
		);
		navigate(
			`/seemp/vessel-revision-grid/${Number(row.original.ImoNumber)}`
		);
	};

	const seempTableColumns = useMemo(
		() => [
			{
				header: " ",
				enableHiding: false,
				enableSorting: false,
				enableColumnActions: false,
				Cell: ({ row }) => {
					return row.original.ReportNumber ? (
						<IconButton
							onClick={() => console.info("Download Zip", row)}
						>
							<Download />
						</IconButton>
					) : (
						""
					);
				},
			},
			{
				header: "WCN",
				enableHiding: false,
				accessorKey: "Wcn",
			},
			{
				header: "Company Name",
				accessorKey: "CompanyName",
			},
			{
				header: "IMO Number",
				enableHiding: false,
				accessorKey: "ImoNumber",
			},
			{
				header: "Vessel Name",
				accessorKey: "VesselName",
			},
			{
				header: "Vessel Type",
				accessorKey: "VesselType",
			},
			{
				header: "Flag",
				accessorKey: "Flag",
			},
			{
				header: "Report Number",
				accessorKey: "ReportNumber",
			},
			{
				header: "Status",
				accessorKey: "Status",
			},
			{
				header: "Submission Date",
				accessorKey: "SubmissionDate",
				accessorFn: (row) => {
					if (row.SubmissionDate) {
						return convertLocaltoUTCFormat(row.SubmissionDate);
					}
				},
			},
			{
				header: "Verified Date",
				accessorKey: "VerifiedDate",
				accessorFn: (row) => {
					if (row.VerifiedDate) {
						return convertLocaltoUTCFormat(row.VerifiedDate);
					}
				},
			},
			{
				header: "ABS Contact",
				accessorKey: "AbsContact",
			},
		],
		[]
	);

	const csvConfig = mkConfig({
		fieldSeparator: ",",
		decimalSeparator: ".",
		useKeysAsHeaders: true,
	});

	const handleExportSeempTable = () => {
		try {
			const formattedData = seempData.map((entry) => ({
				WCN: entry.Wcn || "",
				"Company Name": entry.CompanyName || "",
				"IMO Number": entry.ImoNumber || "",
				"Vessel Name": entry.VesselName || "",
				"Vessel Type": entry.VesselType || "",
				Flag: entry.Flag || "",
				"Report Number": entry.ReportNumber || "",
				Status: entry.Status || "",
				"Submission Date": entry.SubmissionDate || "",
				"Verified Date": entry.VerifiedDate || "",
				"ABS Contact": entry.AbsContact || "",
			}));

			const csv = generateCsv(csvConfig)(formattedData);
			download(csvConfig)(csv);
		} catch (error) {
			// emitToasterMsg("error", "Error exporting data", error);
		}
	};

	const handleSelectAll = (event) => {
		setSelectAll(event.target.checked);
	};

	const seempTable = useMaterialReactTable({
		columns: seempTableColumns,
		data: seempData,
		enableRowSelection: true,
		enableStickyHeader: true,
		manualPagination: true,
		manualFiltering: true,
		manualSorting: true,
		rowCount,
		// enableRowActions: true,
		// positionActionsColumn: 'last',
		// displayColumnDefOptions: {
		//     'mrt-row-actions': {
		//         header: '',
		//     },
		// },
		getRowId: (row) => row.SeempDtlId,
		onRowSelectionChange: setSeempRowSelection,
		onPaginationChange: setSeempPagination,
		onGlobalFilterChange: setSeempGlobalFilter,
		onSortingChange: setSeempSort,
		onColumnFiltersChange: setSeempColumnFilters,
		state: {
			rowSelection: seempRowSelection,
			pagination: seempPagination,
			globalFilter: seempGlobalFilter,
			columnFilters: seempColumnFilters,
			sorting: seempSort,
			isLoading: loading,
		},
		initialState: {
			density: "compact",
		},
		muiTableBodyRowProps: ({ row }) => ({
			onClick: () => handleMyFleetTableRowClick(row),
			sx: {
				cursor: "pointer",
			},
		}),
		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 379px)",
			},
		},
		renderToolbarInternalActions: ({ table }) => (
			<>
				<MRT_ToggleGlobalFilterButton
					id="vessel_view_global_search"
					table={table}
				/>
				<MRT_ShowHideColumnsButton
					id="vessel_view_show_hide_Columns"
					table={table}
				/>
				<MRT_ToggleFiltersButton
					id="vessel_view_global_filter"
					table={table}
				/>
			</>
		),
		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",

				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),
		muiFilterTextFieldProps: {
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
			},
		},
		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},
		muiSelectAllCheckboxProps: {
			title: selectAll ? "Toggle deselect all" : "Toggle select all",
			onClick: (e) => handleSelectAll(e),
			sx: {
				color: "white",
			},
		},
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
	});

	return (
		<div className="seemp-body-wrapper">
			<div className="seemp-actions">
				<Button
					className={"mr-2"}
					startIcon={<Icon icon={ICONS.ADD} />}
				>
					Create MP
				</Button>
				<Button
					startIcon={<Icon icon="ios_share" />}
					variant="secondary"
					onClick={handleExportSeempTable}
				>
					Export Table
				</Button>
			</div>
			<div className="seemp-table-wrapper mt-3">
				<MaterialReactTable table={seempTable} />
			</div>
		</div>
	);
};

export default SeempBody;
